import { isApiErrorBody } from '@ayub/shared'
import type { AuthErrorCode } from '@ayub/shared'
import { getInitDataRaw, isInsideTelegram } from '@/lib/telegram'

/** Thrown by the API client on any non-2xx response, or when the body can't be parsed. */
export class ApiError extends Error {
  readonly statusCode: number
  /** Faqat autentifikatsiya xatolarida to'ldiriladi — WebApp shunga qarab ekran tanlaydi. */
  readonly code?: AuthErrorCode

  constructor(statusCode: number, message: string, code?: AuthErrorCode) {
    super(message)
    this.name = 'ApiError'
    this.statusCode = statusCode
    this.code = code
  }
}

const BASE_URL = '/api'

function buildHeaders(callerHeaders: HeadersInit | undefined, hasBody: boolean): Headers {
  // Chaqiruvchi o'z header'larini qo'sha oladi, lekin Authorization'ni soxtalashtira
  // olmaydi — u har doim shu yerda, initData'dan tekshirilgan holda qo'yiladi.
  const headers = new Headers(callerHeaders)
  // `Content-Type: application/json` FAQAT haqiqiy tana bo'lganda qo'yiladi. Aks holda
  // (tanasiz POST/DELETE — logout, ask-card, admin o'chirish) Fastify so'rovni "JSON deb
  // e'lon qilingan, ammo tana bo'sh" deb HANDLER'GACHA yetkazmay 400 qaytaradi
  // (FST_ERR_CTP_EMPTY_JSON_BODY) — natijada, masalan, logout'ning cookie'ni o'chiruvchi
  // Set-Cookie'si hech qachon yuborilmaydi va foydalanuvchi chiqa olmay qoladi.
  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  const raw = getInitDataRaw()
  if (raw) {
    // Scheme the Fastify server verifies in Phase 5.
    headers.set('Authorization', `tma ${raw}`)
  }
  return headers
}

function extractMessage(body: unknown, fallback: string): string {
  if (isApiErrorBody(body)) {
    return body.error.message
  }
  return fallback
}

async function parseResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()

  if (text.length === 0) {
    if (!response.ok) {
      throw new ApiError(response.status, response.statusText || "So'rov muvaffaqiyatsiz tugadi")
    }
    return undefined as T
  }

  let body: unknown
  try {
    body = JSON.parse(text)
  } catch {
    // Masalan, Caddy'ning handle_errors bloki 502'da oddiy matn qaytaradi — bu JSON emas,
    // shuning uchun buni ApiError'ga aylantiramiz, aks holda UI xom SyntaxError bilan qoladi.
    throw new ApiError(
      response.status,
      "Server javobini o'qib bo'lmadi. Birozdan so'ng qayta urinib ko'ring.",
    )
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      extractMessage(body, response.statusText || "So'rov muvaffaqiyatsiz tugadi"),
      isApiErrorBody(body) ? body.error.code : undefined,
    )
  }

  return body as T
}

// Brauzerda sessiya (cookie, 30 kun) istalgan vaqt tugashi mumkin — masalan admin panelni
// ochiq qoldirib, keyinroq biror amal bajarsa. Bu holatda keyingi so'rov 401 qaytaradi, lekin
// buni HAR bir sahifa/hook alohida ushlab o'tirsa (avval `AuthErrorScreen` xuddi shunday
// ishlatilgan edi — "Yopish" tugmasi Telegram uchun, brauzerda no-op, "Qayta urinish" esa
// yana 401 qaytarib cheksiz siklga tushardi), bu oson unutiladigan va noto'g'ri bajariladigan
// naqsh bo'lardi. Shuning uchun BITTA markaziy joyda ushlanadi: butun sahifa qayta yuklanadi,
// bootstrap (`App.tsx`dagi `BrowserGate` -> `useAuthSession`) yangi cookie holatiga (yo'q)
// qarab avtomatik LoginPage'ni ko'rsatadi — o'lik ekran yoki cheksiz retry sikli bo'lmaydi.
//
// `sessionExpiredTriggered` bayrog'i — bir vaqtda bir nechta so'rov (masalan sahifa bir nechta
// endpointni parallel chaqirsa) barchasi 401 qaytarganda `reload()` faqat BIR marta chaqirilsin
// degani, ortiqcha ish yo'q.
let sessionExpiredTriggered = false

function handleBrowserSessionExpired(): void {
  if (sessionExpiredTriggered) return
  sessionExpiredTriggered = true
  window.location.reload()
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: buildHeaders(init?.headers, init?.body != null),
    // Brauzer rejimida sessiya `HttpOnly` cookie orqali saqlanadi (JS uni o'qiy olmaydi,
    // shuning uchun Telegram initData'dagi kabi `Authorization` sarlavhasiga qo'yib
    // bo'lmaydi — brauzer buni o'zi yuborishi/qabul qilishi kerak). Fetch'ning standart
    // qiymati (`same-origin`) odatda yetarli, lekin buni ANIQ yozib qo'yamiz: dev'da
    // Vite proksi orqali ham, prodda Caddy orqali ham `/api` doim xuddi shu origin'dan
    // ko'rinadi, lekin kelajakda origin ayrilib qolsa (masalan alohida CDN) cookie
    // jimgina tushib qolmasin.
    credentials: 'same-origin',
  })

  // `/auth/*` ATAYLAB chetlab o'tiladi: `/auth/me`ning 401'i "sessiya hali yo'q/hali kirmagan"
  // degani (bu XATO EMAS — `useAuthSession` buni alohida kutadi), "tugadi" emas; `/auth/telegram`
  // (login so'rovining o'zi) 401/403'i esa login xatosi, `LoginPage` o'zi ko'rsatadi. Ikkalasini
  // ham shu yerda "sessiya tugadi" deb qayta yuklasak, `/auth/me` cheksiz reload sikliga
  // tushib qolardi (reload -> bootstrap -> /auth/me -> 401 -> reload -> ...).
  if (response.status === 401 && !isInsideTelegram() && !path.startsWith('/auth/')) {
    handleBrowserSessionExpired()
  }

  return parseResponse<T>(response)
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'GET' })
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PATCH',
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}

// Bosqich 11 (Sozlamalar — `DELETE /api/admins/:id`) gacha bu klientda hech qanday DELETE
// chaqiruvi yo'q edi, shuning uchun wrapper mavjud emas edi. Boshqa metodlar bilan bir xil
// naqsh: tanasi ixtiyoriy (hozircha hech bir DELETE endpoint tana kutmaydi, lekin kelajakda
// kerak bo'lib qolsa qayta yozmaslik uchun shu yerda ham qoldirilgan).
export function apiDelete<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'DELETE',
    body: body === undefined ? undefined : JSON.stringify(body),
  })
}
